import React from 'react';
import { motion } from 'framer-motion';
import { Target, Medal, Flame } from 'lucide-react';
import { EmptyState, getPositionStyle, ClubBadge } from './shared';

export const WeeklyScorersCard = ({ weeklyScorers, isItalian }) => {
  if (!weeklyScorers || weeklyScorers.length === 0) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-[#FF9100]/10 via-[#0a0d15] to-[#0a0d15] border border-[#FF9100]/30 overflow-hidden mb-4"
      data-testid="weekly-scorers-card"
    >
      <div className="px-4 py-3 border-b border-[#FF9100]/20 flex items-center gap-2">
        <Flame size={16} className="text-[#FF9100]" />
        <h3 className="font-heading text-sm uppercase tracking-wider font-bold text-[#FF9100]">
          {isItalian ? 'Marcatori della Settimana' : 'Scorers of the Week'}
        </h3>
        <span className="ml-auto text-[10px] text-slate-500 uppercase tracking-wider">
          {isItalian ? 'Ultimi 7 giorni' : 'Last 7 days'}
        </span>
      </div>
      <div className="divide-y divide-white/5">
        {weeklyScorers.map((p, idx) => (
          <div key={p.player_id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-white/5 transition-colors" data-testid={`weekly-scorer-row-${idx}`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold border ${getPositionStyle(idx)}`}>{idx + 1}</span>
            <ClubBadge logo={p.club_logo} name={p.club_name} colors={p.club_colors} size="xs" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{p.player_name}</p>
              <p className="text-[10px] text-slate-500 truncate">{p.club_name}</p>
            </div>
            <div className="text-center">
              <p className="font-mono font-bold text-base text-[#FF9100]">{p.goals}</p>
              <p className="text-[9px] text-slate-500 uppercase">{isItalian ? 'Gol' : 'Goals'}</p>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

const PlayerList = ({ players, isItalian, icon: Icon, iconColor, title, primaryStat, primaryLabel, secondaryStat, secondaryLabel, primaryColor, testIdPrefix }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid={`${testIdPrefix}-section`}>
    {players.length === 0 ? (
      <EmptyState icon={Icon} text={isItalian ? `Nessun ${testIdPrefix === 'scorers' ? 'marcatore' : 'assistman'} registrato` : `No ${testIdPrefix} registered yet`} />
    ) : (
      <div className="bg-[#0a0d15] border border-white/10 overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
          <Icon size={16} className={iconColor} />
          <h3 className="font-heading text-sm uppercase tracking-wider font-bold">{title}</h3>
        </div>
        <div className="divide-y divide-white/5">
          {players.map((p, idx) => (
            <div key={p.player_id} className="px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors" data-testid={`${testIdPrefix}-row-${idx}`}>
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border ${getPositionStyle(idx)}`}>{idx + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{p.player_name}</p>
                <div className="flex items-center gap-1.5">
                  <ClubBadge logo={p.club_logo} name={p.club_name} colors={p.club_colors} size="xs" />
                  <p className="text-[10px] text-slate-500 truncate">{p.club_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className={`font-mono font-bold text-lg ${primaryColor}`}>{p[primaryStat]}</p>
                  <p className="text-[9px] text-slate-500 uppercase">{primaryLabel}</p>
                </div>
                <div className="text-center">
                  <p className="font-mono text-sm text-slate-400">{p[secondaryStat]}</p>
                  <p className="text-[9px] text-slate-500 uppercase">{secondaryLabel}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </motion.div>
);

export const ScorersSection = ({ topScorers, weeklyScorers, isItalian }) => (
  <div>
    <WeeklyScorersCard weeklyScorers={weeklyScorers} isItalian={isItalian} />
    <PlayerList
      players={topScorers}
      isItalian={isItalian}
      icon={Target}
      iconColor="text-[#00E676]"
      title={isItalian ? 'Classifica Marcatori' : 'Top Scorers'}
      primaryStat="goals"
      primaryLabel={isItalian ? 'Gol' : 'Goals'}
      secondaryStat="assists"
      secondaryLabel="Assist"
      primaryColor="text-[#00E676]"
      testIdPrefix="scorers"
    />
  </div>
);

export const AssistsSection = ({ topAssists, isItalian }) => (
  <PlayerList
    players={topAssists}
    isItalian={isItalian}
    icon={Medal}
    iconColor="text-[#D500F9]"
    title={isItalian ? 'Classifica Assistman' : 'Top Assists'}
    primaryStat="assists"
    primaryLabel="Assist"
    secondaryStat="goals"
    secondaryLabel={isItalian ? 'Gol' : 'Goals'}
    primaryColor="text-[#D500F9]"
    testIdPrefix="assists"
  />
);
